<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import ModalBase from "@/components/modal/ModalBase.vue";
import ModalTabContent from "@/components/modal/ModalTabContent.vue";
import TabsBootstrap from "@/components/TabsBootstrap.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import { routeStore } from "@/_store/routeStore";
import { errorUtils } from "@/utils";
import {
  compactClockLabel,
  formatMinutes,
  formatSecondsAsMinutes,
  formatTimeWindow,
  formatWeight,
  formatVolume,
} from "@/utils/formatters";
import { toSafeString } from "@/utils/strings";

const props = defineProps({
  modalId: { type: String, required: true },
  routeId: { type: String, required: true },
});

const emit = defineEmits(["close"]);

const vuexStore = useStore();
const bootstrap = vuexStore.state.bootstrap;
const routesStore = routeStore();
const isLoading = ref(true);
const snackbar = ref(null);
const currentTab = ref("restrictions");
const tabs = [
  { label: "Restricciones", value: "restrictions" },
  { label: "Ruta", value: "routes" },
];
const tabContentHeight = 110;
const routeDetail = ref(buildEmptyRouteDetail());

function buildEmptyRouteDetail() {
  return {
    _id: "",
    name: "",
    model: "",
    mensajero: null,
    route_color_hex: "",
    stop_time: "",
    max_weight: "",
    max_volume: "",
    km: "",
    time_window: {
      start_time: "",
      end_time: "",
    },
    distance: "",
    total_time: "",
    from_location: {
      address: "",
      label: "",
    },
    to_location: {
      address: "",
      label: "",
    },
  };
}

function extractRoutePayload(value) {
  if (!value || typeof value !== "object") return null;
  if (value.route && typeof value.route === "object") return value.route;
  if (value.data && typeof value.data === "object") {
    if (value.data.route && typeof value.data.route === "object") {
      return value.data.route;
    }
    return value.data;
  }
  return value;
}

function parseFromLocation(value) {
  if (value && typeof value === "object") {
    return {
      address: toSafeString(value.address),
      label: toSafeString(value.label),
    };
  }

  return {
    address: toSafeString(value),
    label: "",
  };
}

function normalizeRouteDetail(value) {
  const route = extractRoutePayload(value) || {};
  return {
    _id: toSafeString(route?._id || route?.id),
    name: toSafeString(route?.name),
    model: toSafeString(route?.model),
    mensajero: route?.mensajero || null,
    route_color_hex: toSafeString(route?.route_color_hex),
    stop_time: route?.stop_time ?? "",
    max_weight: route?.max_weight ?? "",
    max_volume: route?.max_volume ?? "",
    km: route?.km ?? "",
    distance: route?.distance ?? "",
    total_time: route?.total_time ?? "",
    time_window: route?.time_window,
    from_location: parseFromLocation(route?.from_location),
    to_location: parseFromLocation(route?.to_location || route?.from_location),
  };
}

function getRouteId(value) {
  return toSafeString(value?._id || value?.id);
}

function closeSnackbar() {
  snackbar.value = null;
}

function showError(message) {
  snackbar.value = {
    title: "Error",
    message: message || "No se pudo cargar el detalle de la ruta.",
    type: "danger",
    color: "white",
  };
}

function displayValue(value) {
  const normalized = toSafeString(value);
  return normalized || "-";
}

function formatRouteDistance(value) {
  const normalized = toSafeString(value);
  if (!normalized) return "-";
  if (/\bkm\b/i.test(normalized)) return normalized;
  return `${normalized} km`;
}

const stopTimeLabel = computed(() => {
  const value = routeDetail.value?.stop_time;
  if (value === null || value === undefined || toSafeString(value) === "") {
    return "-";
  }
  return formatMinutes(value);
});


const routeNameLabel = computed(() => displayValue(routeDetail.value?.name));
const routeModelLabel = computed(() => displayValue(routeDetail.value?.model));
const mensajeroLabel = computed(() => {
  const mensajero = routeDetail.value?.mensajero;
  const name = toSafeString(mensajero?.name || mensajero?.user);
  const phone = toSafeString(mensajero?.phone);
  if (!name) return phone || "-";
  return phone ? `${name} (${phone})` : name;
});
const maxWeightLabel = computed(() => formatWeight(routeDetail.value?.max_weight));
const maxVolumeLabel = computed(() => formatVolume(routeDetail.value?.max_volume));
const scheduleLabel = computed(() =>
  formatTimeWindow(routeDetail.value?.time_window, compactClockLabel)
);

const distanceLabel = computed(() =>
  formatRouteDistance(routeDetail.value?.km || routeDetail.value?.distance)
);
const totalTimeMinutesLabel = computed(() =>
  formatSecondsAsMinutes(routeDetail.value?.total_time)
);
function formatLocationLabel(location) {
  const address = toSafeString(location?.address);
  const label = toSafeString(location?.label);
  const result = [address, label].filter(Boolean).join(", ");
  return result || "-";
}

const routeAddressLabel = computed(() =>
  formatLocationLabel(routeDetail.value?.from_location)
);
const routeEndAddressLabel = computed(() =>
  formatLocationLabel(routeDetail.value?.to_location)
);

function handleHide() {
  currentTab.value = "restrictions";
  emit("close");
}

function hasCurrentRouteLoaded() {
  const routePayload = extractRoutePayload(routesStore.route);
  if (!routePayload) return false;
  return getRouteId(routePayload) === toSafeString(props.routeId);
}

watch(
  () => routesStore.route,
  (value) => {
    if (!value) return;
    const routePayload = extractRoutePayload(value);
    if (!routePayload) return;
    if (getRouteId(routePayload) !== toSafeString(props.routeId)) return;
    routeDetail.value = normalizeRouteDetail(routePayload);
    isLoading.value = false;
  },
  { immediate: true }
);

watch(
  () => routesStore.error,
  (value) => {
    if (!value) return;
    routeDetail.value = buildEmptyRouteDetail();
    isLoading.value = false;
    showError(
      errorUtils.getErrorMessageHtml(
        value,
        "No se pudo cargar el detalle de la ruta."
      )
    );
  },
  { immediate: true }
);

onMounted(() => {
  isLoading.value = !hasCurrentRouteLoaded();
  closeSnackbar();
  const modal = new bootstrap.Modal(document.getElementById(props.modalId), {
    keyboard: true,
    backdrop: true,
  });
  modal.show();
});
</script>

<template>
  <modal-base :id="modalId" width="40rem" min-height="24rem" @hide="handleHide">
    <template #header>
      <h6 class="mb-0">Detalle</h6>
    </template>

    <template #body>
      <div class="modal-form--wide-labels">
        <div class="row modal-form-row">
          <div class="modal-form-label">
            <span>Alias</span>
          </div>
          <div class="col modal-form-value">
            <span class="modal-display-value">{{ routeNameLabel }}</span>
          </div>
        </div>
        <div class="row modal-form-row">
          <div class="modal-form-label">
            <span>Modelo</span>
          </div>
          <div class="col modal-form-value">
            <span class="modal-display-value">{{ routeModelLabel }}</span>
          </div>
        </div>
        <div class="row modal-form-row">
          <div class="modal-form-label">
            <span
              v-tooltip="'Mensajero activo asignado ahora al vehículo.'"
              >Mensajero <sup class="text-primary sup-question">?</sup></span
            >
          </div>
          <div class="col modal-form-value">
            <span class="modal-display-value">{{ mensajeroLabel }}</span>
          </div>
        </div>

        <div class="row modal-form-row">
          <div class="modal-form-label">
            <span>Inicio de ruta</span>
          </div>
          <div class="col modal-form-value">
            <span class="modal-display-value">{{ routeAddressLabel }}</span>
          </div>
        </div>

        <div class="row modal-form-row">
          <div class="modal-form-label">
            <span>Fin de ruta</span>
          </div>
          <div class="col modal-form-value">
            <span class="modal-display-value">{{ routeEndAddressLabel }}</span>
          </div>
        </div>

        <div class="row modal-form-row">
          <div class="modal-form-label">
            <span>Horario</span>
          </div>
          <div class="col modal-form-value">
            <span class="modal-display-value">{{ scheduleLabel }}</span>
          </div>
        </div>

        <tabs-bootstrap
          v-model="currentTab"
          :tabs="tabs"
          size="sm"
          refined
          style-nav="nav-underline"
          
        />

        <modal-tab-content :height="`${tabContentHeight}px`">
        <div v-show="currentTab === 'restrictions'">
          <div class="row modal-form-row">
            <div class="modal-form-label">
              <span>Tiempo de parada</span>
            </div>
            <div class="col modal-form-value">
              <span class="modal-display-value">{{ stopTimeLabel }}</span>
            </div>
          </div>

          <div class="row modal-form-row">
            <div class="modal-form-label">
              <span>Peso</span>
            </div>
            <div class="col modal-form-value">
              <span class="modal-display-value">{{ maxWeightLabel }}</span>
            </div>
          </div>

          <div class="row modal-form-row">
            <div class="modal-form-label">
              <span>Volumen</span>
            </div>
            <div class="col modal-form-value">
              <span class="modal-display-value">{{ maxVolumeLabel }}</span>
            </div>
          </div>
        </div>

        <div v-show="currentTab === 'routes'"> 
          <div class="row modal-form-row">
            <div class="modal-form-label">
              <span>Kilómetros</span>
            </div>
            <div class="col modal-form-value">
              <span class="modal-display-value">{{ distanceLabel }}</span>
            </div>
          </div>

          <div class="row modal-form-row">
            <div class="modal-form-label">
              <span>Tiempo total</span>
            </div>
            <div class="col modal-form-value">
              <span class="modal-display-value">{{ totalTimeMinutesLabel }}</span>
            </div>
          </div>
        </div>
        </modal-tab-content>
      </div>
    </template>

    <div class="position-fixed top-0 end-0 z-index-2 p-3">
      <argon-snackbar
        v-if="snackbar"
        :title="snackbar.title"
        :description="snackbar.message"
        :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
        :color="snackbar.color"
        :close-handler="closeSnackbar"
      />
    </div>
  </modal-base>
</template>
